import React from 'react';
import Icon from '../../../components/AppIcon';

const FinancialHealthIndicator = ({ formData }) => {
  const calculateHealthScore = () => {
    let score = 0;
    let maxScore = 0;

    // Current Ratio Check (max 25 points)
    maxScore += 25;
    const currentAssets = parseFloat(formData?.currentAssets) || 0;
    const currentLiabilities = parseFloat(formData?.currentLiabilities) || 0;
    if (currentLiabilities > 0) {
      const currentRatio = currentAssets / currentLiabilities;
      if (currentRatio >= 2) score += 25;
      else if (currentRatio >= 1.5) score += 20;
      else if (currentRatio >= 1) score += 15;
      else if (currentRatio >= 0.5) score += 10;
    }

    // Net Margin Check (max 25 points)
    maxScore += 25;
    const revenue = parseFloat(formData?.totalRevenue) || 0;
    const totalExpenses = parseFloat(formData?.totalExpenses) || 0;
    if (revenue > 0) {
      const netMargin = ((revenue - totalExpenses) / revenue) * 100;
      if (netMargin >= 15) score += 25;
      else if (netMargin >= 10) score += 20;
      else if (netMargin >= 5) score += 15;
      else if (netMargin >= 0) score += 10;
    }

    // Cash Reserve Check (max 25 points)
    maxScore += 25;
    const cashReserve = parseFloat(formData?.cashReserve) || 0;
    const dailyOperatingCost = parseFloat(formData?.dailyOperatingCost) || 0;
    if (dailyOperatingCost > 0) {
      const reserveDays = cashReserve / dailyOperatingCost;
      if (reserveDays >= 90) score += 25;
      else if (reserveDays >= 60) score += 20;
      else if (reserveDays >= 30) score += 15;
      else if (reserveDays >= 15) score += 10;
    }

    // Payment Terms Check (max 25 points)
    maxScore += 25;
    const onTimePayment = parseFloat(formData?.onTimePaymentPercentage) || 0;
    if (onTimePayment >= 95) score += 25;
    else if (onTimePayment >= 85) score += 20;
    else if (onTimePayment >= 75) score += 15;
    else if (onTimePayment >= 60) score += 10;

    return maxScore > 0 ? Math.round((score / maxScore) * 100) : 0;
  };

  const healthScore = calculateHealthScore();

  const getHealthStatus = (score) => {
    if (score >= 80) return {
      level: 'Sangat Sehat',
      color: 'success',
      icon: 'CheckCircle2',
      description: 'Kondisi keuangan sangat baik untuk scale-up'
    };
    if (score >= 60) return {
      level: 'Sehat',
      color: 'primary',
      icon: 'ThumbsUp',
      description: 'Kondisi keuangan baik dengan beberapa area perbaikan'
    };
    if (score >= 40) return {
      level: 'Cukup',
      color: 'warning',
      icon: 'AlertCircle',
      description: 'Perlu perbaikan sebelum scale-up'
    };
    return {
      level: 'Berisiko',
      color: 'error',
      icon: 'XCircle',
      description: 'Risiko keuangan tinggi, perlu restrukturisasi'
    };
  };

  const status = getHealthStatus(healthScore);

  const indicators = [
    {
      label: 'Likuiditas',
      value: formData?.currentAssets && formData?.currentLiabilities ? 
        (parseFloat(formData?.currentAssets) / parseFloat(formData?.currentLiabilities))?.toFixed(2) : '0.00',
      target: '≥ 2.0',
      icon: 'Droplet'
    },
    {
      label: 'Profitabilitas',
      value: formData?.totalRevenue && formData?.totalExpenses ?
        (((parseFloat(formData?.totalRevenue) - parseFloat(formData?.totalExpenses)) / parseFloat(formData?.totalRevenue)) * 100)?.toFixed(1) + '%' : '0%',
      target: '≥ 10%',
      icon: 'TrendingUp'
    },
    {
      label: 'Cadangan Kas',
      value: formData?.cashReserve && formData?.dailyOperatingCost ?
        Math.round(parseFloat(formData?.cashReserve) / parseFloat(formData?.dailyOperatingCost)) + ' hari' : '0 hari',
      target: '≥ 90 hari',
      icon: 'Wallet'
    },
    {
      label: 'Pembayaran Tepat Waktu',
      value: (formData?.onTimePaymentPercentage || 0) + '%',
      target: '≥ 95%',
      icon: 'Clock'
    }
  ];

  return (
    <div className="card">
      <div className="flex items-start gap-4 mb-6">
        <div className={`w-12 h-12 rounded-xl bg-${status?.color}/10 flex items-center justify-center flex-shrink-0`}>
          <Icon name="Activity" size={24} color={`var(--color-${status?.color})`} />
        </div>
        <div className="flex-1">
          <h2 className="text-2xl md:text-3xl font-semibold mb-2">Indikator Kesehatan Keuangan</h2>
          <p className="text-sm md:text-base text-muted-foreground">
            Penilaian otomatis kondisi keuangan berdasarkan data yang diinput
          </p>
        </div>
      </div>
      <div className={`p-6 md:p-8 bg-${status?.color}/10 rounded-xl border-2 border-${status?.color}/20 mb-6`}>
        <div className="flex flex-col md:flex-row items-center gap-6">
          <div className={`w-24 h-24 md:w-32 md:h-32 rounded-full bg-${status?.color}/20 flex items-center justify-center flex-shrink-0`}>
            <div className="text-center">
              <p className="text-3xl md:text-4xl font-bold data-text">{healthScore}</p>
              <p className="text-sm text-muted-foreground">/ 100</p>
            </div>
          </div>
          <div className="flex-1 text-center md:text-left">
            <div className="flex items-center justify-center md:justify-start gap-3 mb-2">
              <Icon name={status?.icon} size={28} color={`var(--color-${status?.color})`} />
              <h3 className="text-2xl md:text-3xl font-bold">{status?.level}</h3>
            </div>
            <p className="text-base md:text-lg text-muted-foreground">{status?.description}</p>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {indicators?.map((indicator, index) => (
          <div key={index} className="p-4 md:p-6 bg-muted rounded-lg">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-card flex items-center justify-center">
                <Icon name={indicator?.icon} size={20} color="var(--color-primary)" />
              </div>
              <h4 className="text-base md:text-lg font-semibold">{indicator?.label}</h4>
            </div>
            <div className="flex items-baseline justify-between">
              <p className="text-xl md:text-2xl font-bold data-text">{indicator?.value}</p>
              <p className="text-sm text-muted-foreground">Target: {indicator?.target}</p>
            </div>
          </div>
        ))}
      </div>
      <div className="mt-6 p-4 bg-muted/50 rounded-lg border border-border">
        <div className="flex items-start gap-3">
          <Icon name="Info" size={20} color="var(--color-primary)" className="mt-0.5 flex-shrink-0" />
          <div className="text-sm text-muted-foreground">
            <p className="font-medium mb-1">Catatan Penilaian:</p>
            <p>
              Skor kesehatan dihitung berdasarkan 4 indikator utama: Rasio Lancar, Margin Bersih, Cadangan Kas, dan Disiplin Pembayaran. 
              Data ini akan digunakan AI untuk analisis risiko komprehensif.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FinancialHealthIndicator;